import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMeasure } from '@trinity/chat-components';
import { pdfjs, Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import type { OnDocumentLoadProgress, OnDocumentLoadSuccess } from 'react-pdf/dist/esm/shared/types.js';
import { ProgressOverlay } from '@/components/presentation/ProgressOverlay';
import styles from './style.module.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

type Props = {
  url: string;
  page?: number;
};

export const Pdf = ({ url, page }: Props) => {
  const [progress, setProgress] = useState(0);
  const [numPages, setNumPages] = useState<number>();
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [containerRef, bounds] = useMeasure();
  const [hasScrolledIntoView, setHasScrolledIntoView] = useState(false);
  const [renderedPages, setRenderedPages] = useState<Set<number>>(new Set());

  const options = useMemo(() => ({
    cMapUrl: '/cmaps/',
    cMapPacked: true,
    standardFontDataUrl: '/standard_fonts/',
    // wasmUrl: '/wasm/',
  }), []);

  const onDocumentLoadSuccess: OnDocumentLoadSuccess = document => {
    setNumPages(document.numPages);
  };
  const onProgress: OnDocumentLoadProgress = useCallback(({ loaded, total }) => {
    setProgress((loaded / total) * 100);
  }, []);
  const handleError = useCallback((error: Error) => {
    console.error('Error loading PDF:', error);
  }, []);

  useEffect(() => {
    if (renderedPages.has(page) && !hasScrolledIntoView) {
      pageRefs.current[page - 1]?.scrollIntoView({ behavior: 'instant' });
      setHasScrolledIntoView(true);
    }
  }, [hasScrolledIntoView, renderedPages, page]);

  return (
    <div
      ref={containerRef}
      className={styles.container}>
      <Document
        file={url}
        loading={<Loading progress={progress} />}
        onLoadSuccess={onDocumentLoadSuccess}
        onProgress={onProgress}
        options={options}>
        {Array.from(new Array(numPages), (_el, index) => (
          <Page
            key={`page_${index + 1}`}
            loading={<PageLoading />}
            pageNumber={index + 1}
            className={styles.page}
            inputRef={(el) => { pageRefs.current[index] = el; }}
            onRenderSuccess={() => {
              setRenderedPages(prev => new Set(prev).add(index + 1));
            }}
            width={bounds.width}
          />
        ))}
      </Document>
    </div>
  );
};

type LoadingProps = {
  progress: number;
};

const Loading = memo(({ progress }: LoadingProps) => (
  <div className={styles.progress}>
    <ProgressOverlay progress={progress} />
  </div>
));

const PageLoading = memo(() => (
  <div className={styles.pageLoading}>
    <ProgressOverlay progress={75} />
  </div>
));